import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Flame, Wind, Bell, Truck, ShieldCheck, Plus, Trash2, CheckCircle, AlertCircle, AlertTriangle, Download, Mail, Cloud, Upload, Eye, Clock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import API from '@/config/api';

const TYPE_META = {
  fire_drill: { icon: Flame, color: 'text-red-600', bg: 'bg-red-100' },
  tornado_drill: { icon: Wind, color: 'text-indigo-600', bg: 'bg-indigo-100' },
  smoke_alarm_test: { icon: Bell, color: 'text-amber-600', bg: 'bg-amber-100' },
  vehicle_alarm_test: { icon: Truck, color: 'text-teal-600', bg: 'bg-teal-100' },
  fire_extinguisher: { icon: ShieldCheck, color: 'text-orange-600', bg: 'bg-orange-100' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function SafetyDrillsPage() {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState([]);
  const [drills, setDrills] = useState([]);
  const [radon, setRadon] = useState(null);
  const [radonUploading, setRadonUploading] = useState(false);
  const [year] = useState(new Date().getFullYear());
  const [showLog, setShowLog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    type: 'fire_drill',
    drill_date: new Date().toISOString().split('T')[0],
    drill_time: '',
    duration_minutes: '',
    result: 'pass',
    notes: '',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [statusRes, drillsRes, complianceRes] = await Promise.all([
        axios.get(`${API}/safety-drills/status`, getAuthHeaders()),
        axios.get(`${API}/safety-drills?year=${year}`, getAuthHeaders()),
        axios.get(`${API}/compliance`, getAuthHeaders()),
      ]);
      setStatus(statusRes.data.status || []);
      setDrills(drillsRes.data.drills || []);
      const r = (complianceRes.data || []).find((i) => i.title === 'Radon Testing');
      setRadon(r || null);
    } catch {
      toast.error('Failed to load safety drill data');
    }
  };

  const uploadRadonProof = async (file) => {
    if (!radon || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    setRadonUploading(true);
    try {
      await axios.post(`${API}/compliance/${radon.id}/proof`, fd, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Radon test report uploaded');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setRadonUploading(false);
    }
  };

  const viewRadonProof = async () => {
    if (!radon) return;
    try {
      const res = await axios.get(`${API}/compliance/${radon.id}/proof`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Could not open the file');
    }
  };

  const markRadonComplete = async () => {
    if (!radon) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      await axios.put(`${API}/compliance/${radon.id}?is_completed=true&completed_date=${today}`, {}, getAuthHeaders());
      toast.success('Radon test marked complete for today');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark complete');
    }
  };

  const logDrill = async () => {
    if (!form.drill_date) { toast.error('Drill date required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/safety-drills`, form, getAuthHeaders());
      toast.success('Drill logged');
      if (res.data.warning) toast.message(res.data.warning);
      setShowLog(false);
      setForm({ ...form, drill_time: '', duration_minutes: '', notes: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log drill');
    } finally { setSaving(false); }
  };

  const deleteDrill = async (id) => {
    if (!window.confirm('Delete this drill record?')) return;
    try {
      await axios.delete(`${API}/safety-drills/${id}`, getAuthHeaders());
      toast.success('Drill record deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const downloadReport = async () => {
    try {
      const res = await axios.get(`${API}/safety-drills/report/pdf?year=${year}`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Safety_Drill_Report_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Annual report downloaded');
    } catch {
      toast.error('Failed to download report');
    }
  };

  const sendReminderNow = async () => {
    if (!window.confirm('Send a reminder email now to all admins with missing drills?')) return;
    try {
      await axios.post(`${API}/safety-drills/send-reminder`, {}, getAuthHeaders());
      toast.success('Reminder emails triggered');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const drillsByType = (type) => drills.filter(d => d.type === type);

  return (
    <DashboardLayout title="Safety & Emergency Drills">
      <div className="space-y-6" data-testid="safety-drills-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-heading">Safety & Emergency Drills</h1>
            <p className="text-sm text-muted-foreground">DCF 251.06 — Record monthly drills and annual inspections</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={downloadReport} data-testid="download-annual-report-btn">
              <Download className="w-4 h-4 mr-1" />
              Annual Report PDF
            </Button>
            <Button variant="outline" onClick={sendReminderNow} data-testid="send-reminder-now-btn">
              <Mail className="w-4 h-4 mr-1" />
              Email Reminder Now
            </Button>
            <Button onClick={() => setShowLog(true)} data-testid="log-drill-btn">
              <Plus className="w-4 h-4 mr-1" />
              Log Drill / Test
            </Button>
          </div>
        </div>

        {/* Radon Testing card (state-mandated, not a drill but tracked here for convenience) */}
        {radon && (() => {
          let overdue = false;
          let dueSoon = false;
          let expiresStr = null;
          let daysLeft = null;
          if (radon.completed_date && radon.reset_days) {
            const completed = new Date(radon.completed_date);
            const expires = new Date(completed.getTime() + radon.reset_days * 86400000);
            expiresStr = expires.toISOString().slice(0, 10);
            daysLeft = Math.round((expires - new Date()) / 86400000);
            overdue = daysLeft < 0;
            dueSoon = daysLeft >= 0 && daysLeft <= 60;
          }
          const borderColor = overdue ? 'border-l-red-500' : (dueSoon || !radon.is_completed) ? 'border-l-amber-500' : 'border-l-green-500';
          const yearsLabel = radon.reset_days === 730 ? 'every 2 years' : 'every 5 years';
          return (
            <Card className={cn('border-l-4', borderColor)} data-testid="radon-card">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Cloud className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Radon Testing</CardTitle>
                      <p className="text-xs text-muted-foreground">{radon.regulation_code} — retest {yearsLabel}</p>
                    </div>
                  </div>
                  {overdue ? (
                    <Badge className="bg-red-100 text-red-700 border border-red-200"><AlertTriangle className="w-3 h-3 mr-1" /> Overdue by {Math.abs(daysLeft)} d</Badge>
                  ) : dueSoon ? (
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-200"><Clock className="w-3 h-3 mr-1" /> Due in {daysLeft} d</Badge>
                  ) : radon.is_completed ? (
                    <Badge className="bg-green-100 text-green-700 border border-green-200"><CheckCircle className="w-3 h-3 mr-1" /> Current</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-200"><AlertCircle className="w-3 h-3 mr-1" /> Never tested</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  Wisconsin DCF requires an initial radon test within 6 months of opening
                  and a retest {yearsLabel}. Upload the test report as proof.
                </p>
                {radon.completed_date && (
                  <div className="text-xs text-muted-foreground">
                    Last tested: <span className="text-foreground">{radon.completed_date}</span>
                    {expiresStr && <> · Next retest by <span className="text-foreground">{expiresStr}</span></>}
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {!radon.is_completed && (
                    <Button size="sm" variant="outline" onClick={markRadonComplete} data-testid="radon-mark-complete-btn">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Mark tested today
                    </Button>
                  )}
                  {radon.proof_filename ? (
                    <Button size="sm" variant="outline" onClick={viewRadonProof} data-testid="radon-view-proof-btn">
                      <Eye className="w-3.5 h-3.5 mr-1" /> View report ({radon.proof_filename})
                    </Button>
                  ) : (
                    <label className={cn("inline-flex items-center gap-1 text-sm cursor-pointer text-primary hover:underline", radonUploading && "opacity-50 pointer-events-none")} data-testid="radon-upload-proof-label">
                      <Upload className="w-3.5 h-3.5" /> {radonUploading ? 'Uploading…' : 'Upload test report (PDF or photo)'}
                      <input
                        type="file"
                        className="hidden"
                        accept="application/pdf,image/*"
                        onChange={(e) => uploadRadonProof(e.target.files?.[0])}
                        data-testid="radon-upload-proof-input"
                      />
                    </label>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Status grid - one card per type */}
        <div className="grid md:grid-cols-2 gap-4">
          {status.map((s) => {
            const meta = TYPE_META[s.type] || { icon: ShieldCheck, color: 'text-slate-600', bg: 'bg-slate-100' };
            const Icon = meta.icon;
            const isYearly = s.frequency === 'yearly';
            return (
              <Card
                key={s.type}
                className={cn(
                  'border-l-4',
                  s.is_complete ? 'border-l-green-500' : (s.current_month_due ? 'border-l-amber-500' : 'border-l-slate-300')
                )}
                data-testid={`drill-card-${s.type}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', meta.bg)}>
                        <Icon className={cn('w-5 h-5', meta.color)} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{s.label}</CardTitle>
                        <p className="text-xs text-muted-foreground">{s.regulation_code} — {isYearly ? 'Yearly' : 'Monthly'}</p>
                      </div>
                    </div>
                    {s.is_complete ? (
                      <Badge className="bg-green-100 text-green-700 border border-green-200"><CheckCircle className="w-3 h-3 mr-1" /> Current</Badge>
                    ) : s.current_month_due ? (
                      <Badge className="bg-amber-100 text-amber-700 border border-amber-200"><AlertCircle className="w-3 h-3 mr-1" /> Due</Badge>
                    ) : (
                      <Badge variant="outline">—</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isYearly ? (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {s.last_completed
                          ? `Last inspected: ${new Date(s.last_completed + 'T12:00:00').toLocaleDateString()}`
                          : `No inspection logged for ${year}`
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-12 gap-1">
                        {MONTHS.map((m, i) => {
                          const month = i + 1;
                          const isActive = (s.active_months || []).includes(month);
                          const isCompleted = (s.completed_months || []).includes(month);
                          const isMissed = (s.missing_months || []).includes(month);
                          return (
                            <div
                              key={m}
                              title={`${m}: ${isCompleted ? 'Complete' : isMissed ? 'Missed' : isActive ? 'Required' : 'Not required'}`}
                              className={cn(
                                'h-8 rounded flex items-center justify-center text-[10px] font-semibold',
                                !isActive && 'bg-slate-50 text-slate-300',
                                isActive && isCompleted && 'bg-green-500 text-white',
                                isActive && isMissed && 'bg-red-200 text-red-800',
                                isActive && !isCompleted && !isMissed && 'bg-amber-100 text-amber-700'
                              )}
                              data-testid={`month-${s.type}-${month}`}
                            >
                              {m[0]}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {s.completed_months.length} / {s.active_months?.length || 12} months logged in {year}
                        {s.missing_months.length > 0 && (
                          <span className="text-red-600 font-medium ml-2">
                            • Missing: {s.missing_months.map(m => MONTHS[m - 1]).join(', ')}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Records table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Drill & Test Log — {year}</CardTitle>
            <p className="text-xs text-muted-foreground">{drills.length} record{drills.length === 1 ? '' : 's'}</p>
          </CardHeader>
          <CardContent>
            {drills.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No drills logged yet. Click "Log Drill / Test" to add one.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="mobile-cards w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground uppercase">
                      <th className="py-2 px-2">Type</th>
                      <th className="py-2 px-2">Date</th>
                      <th className="py-2 px-2">Time</th>
                      <th className="py-2 px-2">Duration</th>
                      <th className="py-2 px-2">Result</th>
                      <th className="py-2 px-2">Conducted By</th>
                      <th className="py-2 px-2">Notes</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {drills.map(d => (
                      <tr key={d.id} className="border-b hover:bg-muted/30" data-testid={`drill-row-${d.id}`}>
                        <td className="py-2 px-2">
                          <Badge variant="outline">{d.label}</Badge>
                        </td>
                        <td className="py-2 px-2">{new Date(d.drill_date + 'T12:00:00').toLocaleDateString()}</td>
                        <td className="py-2 px-2">{d.drill_time || '—'}</td>
                        <td className="py-2 px-2">{d.duration_minutes ? `${d.duration_minutes} min` : '—'}</td>
                        <td className="py-2 px-2">
                          <Badge className={d.result === 'pass' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {d.result || '—'}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 text-xs">{d.conducted_by_name || '—'}</td>
                        <td className="py-2 px-2 text-xs text-muted-foreground max-w-xs truncate" title={d.notes}>{d.notes || '—'}</td>
                        <td className="py-2 px-2">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteDrill(d.id)} data-testid={`delete-drill-${d.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Log dialog */}
        <Dialog open={showLog} onOpenChange={setShowLog}>
          <DialogContent className="max-w-lg" data-testid="log-drill-dialog">
            <DialogHeader>
              <DialogTitle>Log Drill / Test</DialogTitle>
              <DialogDescription>Record a completed safety drill or test for DCF compliance.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="drill-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(TYPE_META).map(id => (
                      <SelectItem key={id} value={id}>
                        {(status.find(s => s.type === id) || {}).label || id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={form.drill_date} onChange={(e) => setForm({ ...form, drill_date: e.target.value })} data-testid="drill-date-input" />
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input type="time" value={form.drill_time} onChange={(e) => setForm({ ...form, drill_time: e.target.value })} data-testid="drill-time-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Duration (min)</Label>
                  <Input type="number" min="0" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value ? Number(e.target.value) : '' })} placeholder="e.g. 3" data-testid="drill-duration-input" />
                </div>
                <div>
                  <Label className="text-xs">Result</Label>
                  <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
                    <SelectTrigger data-testid="drill-result-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pass">Pass</SelectItem>
                      <SelectItem value="fail">Fail — needs follow-up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. 3 min to evacuate, all kids accounted for" rows={3} data-testid="drill-notes-input" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowLog(false)}>Cancel</Button>
                <Button onClick={logDrill} disabled={saving} data-testid="save-drill-btn">
                  {saving ? 'Saving…' : 'Save Drill'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
